import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../api/axiosInstance";

export const useWizardTestCaseSummary = (payload, options = {}) => {
  return useQuery({
    queryKey: ['wizard-test-case-summary', payload],
    queryFn: async () => {
      const response = await axiosInstance.post(
        '/wizard/get_wizard_test_case_summary',
        payload
      );
      return response.data;
    },
    ...options
  });
};


export const useDownloadAgent = () => {
  return useMutation({
    mutationFn: async ({ os, agent_type, device_name }) => {
      const response = await axiosInstance.get(`/crawler/latest_agent?os=${os}&agent_type=${agent_type}
    
        `);
      return response;
    },
  });
};

export const useSelectInteractionMode = () => {
  return useMutation({
    mutationFn: async ({ projectId, interactionMode }) => {
      return axiosInstance.patch(
        `/projects/interaction-mode`,
        { interaction_mode: interactionMode },
        { params: { project_id: projectId } }
      ).then(res => res.data);
    }
  });
};


export const useGeneratePairingToken = () => {
  return useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post(`/crawler/generate_lab_token`);
      return res;
    }
  });
};

export const useVerifyPairingToken = () => {
  return useMutation({
    mutationFn: async (payload) => {
      try {
        const response = await axiosInstance.post(
          `/crawler/verify_lab_token`,
          payload
        ); 
        return response; 
      } catch (error) {
        throw error; 
      }
    }
  });
};


export const useAddIOTDevice =()=>{
  return useMutation({
    mutationFn: async (payload) => {
      const response = await axiosInstance.post(
        `/iot/add_iot`,
         payload
      );
      return response;
    }
  })
}


export const useGetServices=({device_id})=>{
  return useQuery({
    queryKey: ['services'],
    queryFn: async () => {
      const response = await axiosInstance.get(`/iot/get_iot_services?device_id=${device_id}`);
      return response;
    },
  })
}


  export const useCreateIOTTestCase = (projectId) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (payload) => {
        const response = await axiosInstance.post(
          `/iot/create_iot_test_case`,
          payload
        );
        return response;
      },

      onSuccess: () => {
        queryClient.invalidateQueries(['iot-test-cases', projectId]);
      },
    });
  };


  export const useGetIOTTestCases = ({ projectId, enabled }) => {
    return useQuery({
      queryKey: ['iot-test-cases', projectId],
      queryFn: async () => {
        const response = await axiosInstance.get(
          `/test_case/cases_list?project_id=${projectId}&page=1&page_size=10&is_wizard=false`
        );
        return response;
      },
      enabled: enabled ?? true 
    });
  };

export const useIotDeviceData = (projectId, options = {}) => {
  return useQuery({
    queryKey: ['iot-device-data', projectId], // unique key
    queryFn: async () => {
      if (!projectId) return null; // prevent invalid API call
      const response = await axiosInstance.get(
        `/iot/iot_device_data?project_id=${projectId}`
      );
      return response ?? null; // never return undefined
    },
    enabled: !!projectId, // run only when projectId exists
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    ...options
  });
};

export const useAddIotServices = () => {
  return useMutation({
    mutationFn: async ({ deviceId, servicesPayload }) => {
      const response = await axiosInstance.post(
        `/iot/add_iot_services?device_id=${deviceId}`,
        servicesPayload
      );
      return response.data;
    }
  });
};

export const useGetIotServices = (deviceId, options = {}) => {
  return useQuery({
    queryKey: ['iot-services', deviceId],
    queryFn: async () => {
      if (!deviceId) return null;
      const response = await axiosInstance.get(
        `/iot/get_iot_services?device_id=${deviceId}`
      );
      return response ?? [];
    },
    enabled: !!deviceId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    ...options
  });
};

export const useSaveExecutionScripts = () => {
  return useMutation({
    mutationFn: async (payload) => {
      const response = await axiosInstance.post(
        `/iot/save_execution_scripts`,
        payload
      );
      return response.data;
    }
  });
};

export const useGetExecutionStats = (projectId, options = {}) => {
  return useQuery({
    queryKey: ['iot-execution-stats', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      const response = await axiosInstance.get(
        `/iot/execution_stats?project_id=${projectId}`
      );
      return response;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    ...options
  });
};

export const useStartUIDiscovery = () => {
  return useMutation({
    mutationFn: async ({ projectId, payload }) => {
      const response = await axiosInstance.post(
        '/crawler/discovery/start',
        payload,
        { params: { project_id: projectId } }
      );
      return response;
    }
  });
};

export const useCrawlerDiscoveryOverview = (projectId, options = {}) => {
  return useQuery({
    queryKey: ['crawler-discovery-overview', projectId],
    queryFn: async () => {
      if (!projectId) return null;
      const response = await axiosInstance.get('/crawler/discovery/overview', {
        params: { project_id: projectId }
      });
      return response?.data ?? null;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    ...options
  });
};

const pickFirstArray = (...candidates) => candidates.find(Array.isArray) || [];

const toNonNegativeInt = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? Math.floor(num) : null;
};

/**
 * Normalizes one page of the `crawler/screenshots` response into a predictable shape.
 * The list may arrive as `data`, `data.screenshots`, `screenshots`, ... and the pagination
 * metadata may be nested under `pagination` or sit at the top level, so we look in each place.
 */
export const normalizeCrawlerScreenshotsPage = (response, requestedPage = 1, pageSize = 12) => {
  const body = response && typeof response === 'object' ? response : {};
  const data = body.data;
  const dataObject = data && typeof data === 'object' && !Array.isArray(data) ? data : {};

  const screenshots = pickFirstArray(
    data,
    dataObject.screenshots,
    dataObject.items,
    dataObject.results,
    body.screenshots,
    body.items,
    body.results,
    response
  );

  const isMetaObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  const pagination =
    [body.pagination, dataObject.pagination, body.meta, dataObject.meta].find(isMetaObject) ||
    (Object.keys(dataObject).length > 0 ? dataObject : body);

  const currentPage = toNonNegativeInt(pagination.current_page ?? pagination.page) || requestedPage;
  const totalItems = toNonNegativeInt(
    pagination.total_items ?? pagination.total_records ?? pagination.total_count ?? pagination.total
  );
  const totalPages =
    toNonNegativeInt(pagination.total_pages) ??
    (totalItems !== null && pageSize > 0 ? Math.ceil(totalItems / pageSize) : null);

  let hasNextPage;
  if (typeof pagination.has_next_page === 'boolean') {
    hasNextPage = pagination.has_next_page;
  } else if (typeof pagination.has_next === 'boolean') {
    hasNextPage = pagination.has_next;
  } else if (totalPages !== null) {
    hasNextPage = currentPage < totalPages;
  } else {
    hasNextPage = screenshots.length > 0 && screenshots.length >= pageSize;
  }

  return { screenshots, currentPage, totalPages, totalItems, hasNextPage };
};

export const useCrawlerScreenshots = (projectId, { pageSize = 12, enabled = true } = {}) => {
  return useInfiniteQuery({
    queryKey: ['crawler-screenshots', projectId, pageSize],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axiosInstance.get('/crawler/screenshots', {
        params: { project_id: projectId, page: pageParam, page_size: pageSize },
        skipGlobalLoader: true,
        skipGlobalToast: true
      });
      return normalizeCrawlerScreenshotsPage(response, pageParam, pageSize);
    },
    getNextPageParam: (lastPage) => (lastPage?.hasNextPage ? lastPage.currentPage + 1 : undefined),
    initialPageParam: 1,
    enabled: !!projectId && enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: 1
  });
};
